// FIG Artifacts (DESIGN_SPEC §11.4.11). Props: FigProps. One full-width journal cell.
//   Every file the node stored in one dense table, ordered by kind (sample · dropped · dump · memory · capture · replay ·
//   screen · report · other) then by time: kind · name · size · sha-256 8…8 + copy · stored (run clock; the event's
//   time when the store's clock precedes the event that made the file, both in the tooltip) · linked to (the process,
//   persistence, YARA, the child analysis; one line, the rest in the tooltip) · download (presigned, quiet same-origin
//   fallback). Kind chips with counts filter the table when there are several kinds.
//   A dropped file the sandbox can run and no child analysis covers offers "analyze as a child run": its bytes are
//   resubmitted with the parent's settings and parent_job_id (Turnstile when the backend asks for it).
//   Screens open the full-size frame viewer.
//   meta(ctx) → { id: "artifacts", label: "Artifacts", count: files, empty: none stored }
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Chip, ChipGroup, CopyButton, FigHead, Hash, Icon, LiveBadge, MT, QueuedBadge, Sep, Verdict, figTitleId } from "@/components/ui";
import { LINUX_EXTS, WINDOWS_EXTS } from "@/features/submit/inferProfile";
import { storedFile, useArmedSubmit } from "@/features/submit/useArmedSubmit";
import { useChildJobs, usePresignedDownload } from "@/hooks";
import type { SubmitSpec } from "@/lib/api";
import { bytes, count, rel, utc } from "@/lib/format";
import type { FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import { liveRecord } from "@/lib/records";
import type { Artifact, Event, Job } from "@/types";
import { treeOf, type Tree } from "../derive/tree";
import { FrameViewer, sessionFrames } from "./SessionFig";

export function meta(ctx: ReportCtx): FigMeta {
  return { id: "artifacts", label: "Artifacts", count: ctx.artifacts.length, empty: ctx.artifacts.length === 0 };
}

/** Short kind words, in table order; an unknown kind keeps its own name, after these. */
const KINDS: { word: string; plural: string; kinds: string[] }[] = [
  { word: "sample", plural: "sample", kinds: ["sample"] },
  { word: "dropped", plural: "dropped", kinds: ["dropped-file"] },
  { word: "dump", plural: "dumps", kinds: ["process-dump"] },
  { word: "memory", plural: "memory", kinds: ["memory-dump"] },
  { word: "capture", plural: "capture", kinds: ["pcap"] },
  { word: "replay", plural: "replay", kinds: ["replay"] },
  { word: "screen", plural: "screens", kinds: ["screenshot"] },
  { word: "report", plural: "reports", kinds: ["static-report", "behavior-report", "config-report", "memdump-report"] },
];
const kindOf = (kind: string) => {
  const i = KINDS.findIndex((k) => k.kinds.includes(kind));
  return i >= 0 ? { order: i, word: KINDS[i].word, plural: KINDS[i].plural } : { order: KINDS.length, word: kind || "other", plural: kind || "other" };
};

const base = (p: string) => (p.split(/[\\/]/).pop() ?? p).toLowerCase();
const str = (v: unknown) => (typeof v === "string" ? v : "");

// ---- what the run says about each file -------------------------------------------------------------------------
type Index = {
  t0: number;
  writes: Map<string, { path: string; pid: number; ts: number }>;
  starts: Map<string, { pid: number; ts: number }>;
  loads: Map<string, { pid: number; ts: number }>;
  runKeys: Set<string>;
  tasks: Set<string>;
  services: Set<string>;
  yara: Map<string, string[]>;
  dumps: Map<string, { pid: number; reason: string; ts: number }>;
  flows: number;
  dns: number;
};

function indexRun(events: readonly Event[], t0: number): Index {
  const ix: Index = { t0, writes: new Map(), starts: new Map(), loads: new Map(), runKeys: new Set(), tasks: new Set(), services: new Set(), yara: new Map(), dumps: new Map(), flows: 0, dns: 0 };
  const names = (s: string) => s.toLowerCase().match(/[a-z0-9_.-]+\.(exe|dll|ps1|vbs|js|bat|cmd|scr|dat|sys)\b/g) ?? [];
  for (const e of events) {
    const f = e.fields ?? {};
    const ts = Date.parse(e.ts);
    switch (e.kind) {
      case "file.create":
      case "file.write":
      case "file.drop": {
        const p = str(f.path);
        if (p && !ix.writes.has(base(p))) ix.writes.set(base(p), { path: p, pid: Number(f.pid), ts });
        break;
      }
      case "file.rename": {
        const p = str(f.to);
        if (p && !ix.writes.has(base(p))) ix.writes.set(base(p), { path: p, pid: Number(f.pid), ts });
        break;
      }
      case "proc.start": {
        const img = str(f.image);
        if (img && !ix.starts.has(base(img))) ix.starts.set(base(img), { pid: Number(f.pid), ts });
        const cmd = str(f.cmdline);
        if (/schtasks/i.test(cmd) && /\/create/i.test(cmd)) names(cmd).forEach((n) => ix.tasks.add(n));
        break;
      }
      case "image.load": {
        const img = str(f.image);
        if (img && !ix.loads.has(base(img))) ix.loads.set(base(img), { pid: Number(f.pid), ts });
        break;
      }
      case "registry.set": {
        const key = str(f.key);
        const val = str(f.value);
        if (/\\currentversion\\run(once)?(\\|$)/i.test(key)) names(val).forEach((n) => ix.runKeys.add(n));
        else if (/\\services\\/i.test(key)) names(val).forEach((n) => ix.services.add(n));
        break;
      }
      case "yara.match": {
        const rule = str(f.rule);
        for (const k of [str(f.artifact).toLowerCase(), str(f.sha256).toLowerCase()].filter(Boolean)) ix.yara.set(k, [...(ix.yara.get(k) ?? []), rule]);
        break;
      }
      case "dump.taken": {
        const name = str(f.name);
        if (name) ix.dumps.set(name.toLowerCase(), { pid: Number(f.pid), reason: str(f.reason), ts });
        break;
      }
      case "net.flow":
        ix.flows++;
        break;
      case "net.dns":
        ix.dns++;
        break;
      default:
        break;
    }
  }
  return ix;
}

const procName = (tree: Tree, pid: number) => {
  const key = tree.byPid.get(pid)?.[0];
  return key ? tree.nodes.get(key)?.name : undefined;
};
const pidText = (tree: Tree, pid: number) => {
  const n = procName(tree, pid);
  return n ? `${n} ${pid}` : `pid ${pid}`;
};

function ChildLink({ child }: { child: Job }) {
  const v = child.verdict;
  const live = liveRecord(child, Date.now());
  return (
    <Link className="rowlink inline-flex items-center gap-2" to={`/jobs/${child.id}`} viewTransition>
      child analysis
      {v ? <Verdict v={v.label} score={v.score} /> : live ? <LiveBadge size="sm" /> : child.state === "queued" ? <QueuedBadge size="sm" /> : <span className="text-ink-faint">{child.state}</span>}
    </Link>
  );
}

/** A dropped file the guest profiles can run (the Submit page's extension sets). */
function runnable(name: string): boolean {
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
  return WINDOWS_EXTS.has(ext) || LINUX_EXTS.has(ext);
}

/** Queue a dropped file as this run's child (parent_job_id), with the parent's guest, network, timeout and labels. */
function AnalyzeChild({ parent, a }: { parent: Job; a: Artifact }) {
  const run = useArmedSubmit(async () => {
    const spec: SubmitSpec = { profile: parent.profile, network: parent.network, timeout_secs: parent.timeout_secs, parent_job_id: parent.id };
    if (parent.labels?.length) spec.labels = parent.labels;
    return { file: await storedFile(parent.id, a.sha256, a.name), spec };
  }, `/jobs/${parent.id}`);
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      <button type="button" className="lnk" disabled={run.working} onClick={run.arm} title={`Submit ${a.name} as a child of this run, with the same guest, network and timeout`}>
        {run.working ? <span className="breathe">{run.checking ? "checking you're human…" : "submitting…"}</span> : "analyze as a child run"}
      </button>
      {run.working && run.check && <div ref={run.widget} className="basis-full empty:hidden" />}
      {run.error && (
        <span className="basis-full whitespace-normal text-ink" role="alert">
          {run.error.head}: {run.error.detail}
          {run.error.login && (
            <>
              {" "}
              <a className="lnk" href={run.signIn}>
                Sign in with GitHub
              </a>
            </>
          )}
        </span>
      )}
    </span>
  );
}

function linkedTo(a: Artifact, ix: Index, tree: Tree, child: Job | undefined, shots: readonly Artifact[], parent: Job): ReactNode[] {
  const out: ReactNode[] = [];
  const name = a.name.toLowerCase();
  const rules = [...new Set([...(ix.yara.get(name) ?? []), ...(ix.yara.get(a.sha256.toLowerCase()) ?? [])])];
  switch (a.kind) {
    case "sample": {
      const s = ix.starts.get(name);
      out.push(s ? `started as pid ${s.pid}` : "the submitted file");
      break;
    }
    case "dropped-file": {
      const w = ix.writes.get(name);
      if (w) out.push(`written by ${pidText(tree, w.pid)}`);
      const s = ix.starts.get(name);
      if (s) out.push(`started as pid ${s.pid}`);
      const l = ix.loads.get(name);
      if (!s && l) out.push(`loaded by ${pidText(tree, l.pid)}`);
      if (ix.runKeys.has(name)) out.push("Run key");
      if (ix.tasks.has(name)) out.push("scheduled task");
      if (ix.services.has(name)) out.push("service");
      break;
    }
    case "process-dump": {
      const d = ix.dumps.get(name);
      const pid = d?.pid ?? Number(/(\d+)/.exec(a.name)?.[1]);
      if (Number.isFinite(pid)) out.push(pidText(tree, pid));
      if (d?.reason) out.push(d.reason.replace(/^thread\.inject /, "injection ").replace(/_/g, " "));
      break;
    }
    case "memory-dump":
      out.push("full guest memory");
      break;
    case "pcap":
      out.push(ix.flows || ix.dns ? `${count(ix.flows)} flows · ${count(ix.dns)} DNS` : "full capture");
      break;
    case "replay":
      out.push("played in Session");
      break;
    case "screenshot": {
      const i = shots.findIndex((s) => s.sha256 === a.sha256);
      out.push(i >= 0 ? `frame ${i + 1} of ${shots.length}` : "a stored frame");
      break;
    }
    case "static-report":
      out.push("decoded in Static PE");
      break;
    case "behavior-report":
      out.push("tags and indicators");
      break;
    case "config-report":
      out.push("decoded in Config");
      break;
    case "memdump-report":
      out.push("memory analysis");
      break;
    default:
      break;
  }
  // what matters most leads, so a clipped line keeps it: the child analysis (or the action), then YARA, then the rest
  const lead: ReactNode[] = [];
  if (child) lead.push(<ChildLink key="child" child={child} />);
  else if (a.kind === "dropped-file" && runnable(a.name)) lead.push(<AnalyzeChild key="analyze" parent={parent} a={a} />);
  for (const r of rules) lead.push(<span key={`y:${r}`} className="text-critical-text">YARA {r}</span>);
  return [...lead, ...out];
}

/** Stored time on the run clock; when the store's clock precedes the event that made the file, show the event's time. */
function stored(a: Artifact, ix: Index): { text: string; title: string } {
  const ts = Date.parse(a.created_at);
  const w = a.kind === "dropped-file" ? ix.writes.get(a.name.toLowerCase()) : undefined;
  const dumped = a.kind === "process-dump" ? ix.dumps.get(a.name.toLowerCase()) : undefined;
  const ev = w?.ts ?? dumped?.ts;
  const at = (t: number) => (Number.isFinite(ix.t0) ? rel(t - ix.t0, 1) : utc(t, "time"));
  if (ev !== undefined && Number.isFinite(ev) && Number.isFinite(ts) && ts < ev)
    return { text: at(ev), title: `stored ${utc(ts, "time-ms")} UTC, before the event at ${utc(ev, "time-ms")} UTC` };
  if (!Number.isFinite(ts)) return { text: "—", title: "" };
  return { text: at(ts), title: `${utc(ts, "iso")}` };
}

// ---- view ----------------------------------------------------------------------------------------------------
function Row({
  a,
  ix,
  tree,
  child,
  shots,
  job,
  onFrame,
  pending,
  download,
}: {
  a: Artifact;
  ix: Index;
  tree: Tree;
  child?: Job;
  shots: readonly Artifact[];
  job: Job;
  onFrame: (i: number) => void;
  pending: string | null;
  download: (jobId: string, a: Artifact) => Promise<unknown>;
}) {
  const st = stored(a, ix);
  const links = linkedTo(a, ix, tree, child, shots, job);
  const frame = a.kind === "screenshot" ? shots.findIndex((s) => s.sha256 === a.sha256) : -1;
  const plain = links.filter((l): l is string => typeof l === "string").join(" · ");
  return (
    <tr>
      <td className="text-ink-mute" translate="no" title={a.kind}>
        {kindOf(a.kind).word}
      </td>
      <td className="primary max-w-[34ch]" title={a.name}>
        {frame >= 0 ? (
          <button type="button" className="lnk max-w-full border-b-0 text-left" onClick={() => onFrame(frame)} aria-label={`Open ${a.name} full size`}>
            <MT value={a.name} tail={10} />
          </button>
        ) : (
          <span className="block" translate="no">
            <MT value={a.name} tail={10} />
          </span>
        )}
      </td>
      <td className="r">{bytes(a.size)}</td>
      <td>
        <span className="inline-flex items-center gap-1">
          <Hash value={a.sha256} short />
          <CopyButton value={a.sha256} label={`SHA-256 of ${a.name}`} />
        </span>
      </td>
      <td className="r" title={st.title}>
        {st.text}
      </td>
      <td className="w-full max-w-0 min-w-[28ch]" title={plain || undefined}>
        {links.length ? (
          <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
            {links.map((l, i) => (
              <Fragment key={i}>
                {i > 0 && <Sep />}
                {l}
              </Fragment>
            ))}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        )}
      </td>
      <td className="r">
        <button type="button" className="copy" aria-label={`Download ${a.name}`} title={`Download ${a.name}`} disabled={pending === a.sha256} onClick={() => void download(job.id, a)}>
          {pending === a.sha256 ? <span className="breathe text-micro">…</span> : <Icon name="download" size={13} />}
        </button>
      </td>
    </tr>
  );
}

export default function ArtifactsFig({ ctx, no }: FigProps) {
  const { job, artifacts } = ctx;
  const events = ctx.stream.snap.events;
  const version = ctx.stream.snap.version;
  const t0 = ctx.stream.t0 ?? (events.length ? Date.parse(events[0].ts) : NaN);
  const ix = useMemo(() => indexRun(events, t0), [events, version, t0]);
  const tree = treeOf(ctx);
  const children = useChildJobs(job);
  const bySha = useMemo(() => new Map(children.map((c) => [c.sample_sha ?? "", c])), [children]);
  const shots = useMemo(() => sessionFrames(artifacts), [artifacts]);
  const { download, pending } = usePresignedDownload();
  const [viewing, setViewing] = useState<number | null>(null);
  const [only, setOnly] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      [...artifacts].sort((a, b) => kindOf(a.kind).order - kindOf(b.kind).order || kindOf(a.kind).word.localeCompare(kindOf(b.kind).word) || Date.parse(a.created_at) - Date.parse(b.created_at)),
    [artifacts],
  );
  const kinds = useMemo(() => {
    const m = new Map<string, { plural: string; n: number }>();
    for (const a of rows) {
      const k = kindOf(a.kind);
      const cur = m.get(k.word);
      if (cur) cur.n++;
      else m.set(k.word, { plural: k.plural, n: 1 });
    }
    return [...m.entries()];
  }, [rows]);
  const shown = only ? rows.filter((a) => kindOf(a.kind).word === only) : rows;

  const n = artifacts.length;
  const total = artifacts.reduce((s, a) => s + (Number(a.size) || 0), 0);

  return (
    <section className="cell fig" aria-labelledby={figTitleId(no)}>
      <FigHead no={no} label="Artifacts" count={`${count(n)} ${n === 1 ? "file" : "files"} · ${bytes(total)}`} />
      {kinds.length > 2 && (
        <ChipGroup label="Artifact kinds" className="mt-4">
          <Chip pressed={only === null} all count={count(n)} onClick={() => setOnly(null)}>
            all
          </Chip>
          {kinds.map(([word, k]) => (
            <Chip key={word} pressed={only === word} count={count(k.n)} className="chip--pick" onClick={() => setOnly(only === word ? null : word)}>
              {k.plural}
            </Chip>
          ))}
        </ChipGroup>
      )}
      {/* relative: the copy buttons' absolutely positioned status text must stay inside the scroller */}
      <div className="tscroll relative mt-4">
        <table className="t t--sm">
          <thead>
            <tr>
              <th>kind</th>
              <th>name</th>
              <th className="r">size</th>
              <th>sha-256</th>
              <th className="r">stored</th>
              <th>linked to</th>
              <th className="r">
                <span className="sr-only">download</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((a) => (
              <Row
                key={`${a.kind}:${a.sha256}:${a.name}`}
                a={a}
                ix={ix}
                tree={tree}
                child={a.kind === "dropped-file" ? bySha.get(a.sha256) : undefined}
                shots={shots}
                job={job}
                onFrame={setViewing}
                pending={pending}
                download={download}
              />
            ))}
          </tbody>
        </table>
      </div>
      <FrameViewer jobId={job.id} frames={shots} index={viewing} t0={ctx.stream.t0} onIndex={setViewing} onClose={() => setViewing(null)} />
    </section>
  );
}
